import React, { useState, useRef, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { useLanguage } from '../../i18n/index.jsx'
import useTipsStore from '../../store/tipsStore.js'
import EndDemoButton from './EndDemoButton.jsx'
import FeedbackButton from './FeedbackButton.jsx'
import ChatBubble from '../chat/ChatBubble.jsx'

const STORAGE_KEY = 'brighella.floatingActions.expanded'

// AI chat is re-enabled for TESTING (routed to the standard non-EU endpoint
// with a sensitive-data warning). The bubble shows by default and can be
// explicitly switched off with VITE_AI_CHAT_ENABLED=false without code changes.
const AI_CHAT_ENABLED = import.meta.env.VITE_AI_CHAT_ENABLED !== 'false'

/**
 * Speed-dial cluster, bottom-right. A single FAB expands a vertical stack of
 * action pills (End-Demo, Feedback, Tips, AI Chat). Expanded/collapsed state
 * persists in localStorage. The action components stay mounted while collapsed
 * so an open chat panel or feedback modal survives a collapse.
 */
export default function FloatingActions() {
  const { t } = useLanguage()
  const { enabled: tipsEnabled, toggle: toggleTips } = useTipsStore()
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true' } catch { return false }
  })
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(expanded)) } catch { /* storage unavailable */ }
  }, [expanded])

  // Collapse on click-away or Escape while expanded.
  useEffect(() => {
    if (!expanded) return undefined
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setExpanded(false)
    }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setExpanded(false) }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [expanded])

  // Items animate in/out; nearest the FAB (indexFromBottom 0) leads the stagger.
  const itemAnim = (indexFromBottom: number): React.CSSProperties => {
    const delay = expanded ? indexFromBottom * 0.04 : 0
    return {
      opacity: expanded ? 1 : 0,
      transform: expanded ? 'translateY(0)' : 'translateY(8px)',
      pointerEvents: expanded ? 'auto' : 'none',
      transition: `opacity 0.18s ease ${delay}s, transform 0.18s ease ${delay}s`,
    }
  }

  return (
    <div
      ref={containerRef}
      className="floating-actions"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 'var(--z-chat)',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10,
        pointerEvents: 'none',
      }}
    >
      {/* Action stack — kept mounted so open chat/feedback panels survive a collapse */}
      <EndDemoButton triggerStyle={itemAnim(4)} />
      <FeedbackButton triggerStyle={itemAnim(3)} />
      <button
        className="floating-action-btn"
        onClick={toggleTips}
        aria-label={tipsEnabled ? t('tipsDisabled') : t('tipsEnabled')}
        title={tipsEnabled ? 'Hide tips' : 'Show tips'}
        style={{
          height: 36, padding: '0 14px', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          background: tipsEnabled ? 'var(--accent)' : 'var(--bg-surface)',
          color: tipsEnabled ? '#e2e8f0' : 'var(--text-muted)',
          fontSize: 'var(--text-sm)', fontWeight: 500, cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 6,
          ...itemAnim(2),
        }}
      >
        💡 {t('tips')}
      </button>
      {/* Keyboard-shortcuts handle — dispatches `?` keydown that KeyboardHelp
          (mounted in AppLayout) listens for. Moved here from the sidebar so
          the sidebar toolbar isn't overcrowded. */}
      <button
        className="floating-action-btn"
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }))}
        aria-label={t('kbdHelpOpenTitle')}
        title={t('kbdHelpOpenTitle')}
        style={{
          height: 36, padding: '0 14px', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)',
          background: 'var(--bg-surface)', color: 'var(--text-muted)',
          fontSize: 'var(--text-sm)', fontWeight: 500, cursor: 'pointer',
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 6,
          ...itemAnim(1),
        }}
      >
        ⌨ {t('kbdShortcuts')}
      </button>
      {AI_CHAT_ENABLED && <ChatBubble triggerStyle={itemAnim(0)} />}

      {/* Toggle FAB — Plus rotates 45° into an × when expanded */}
      <button
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
        aria-label={t('quickActions')}
        title={t('quickActions')}
        style={{
          width: 44, height: 44, borderRadius: '50%',
          border: '1px solid var(--border-default)',
          background: 'var(--accent)', color: 'var(--text-primary)',
          cursor: 'pointer', pointerEvents: 'auto',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
        }}
      >
        <Plus
          size={22}
          style={{ transition: 'transform 0.2s ease', transform: expanded ? 'rotate(45deg)' : 'none' }}
        />
      </button>
    </div>
  )
}
